import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { useLandingContent } from "@/features/admin/content/landingContent";

const isExternal = (h: string) => /^https?:\/\//.test(h);

/**
 * Precios — planes editables + switch Mensual/Anual. En modo anual se muestra el
 * precio rebajado (por mes facturado anual) y una etiqueta de ahorro por plan,
 * para que quede clara la rebaja al elegir el plan anual.
 *
 * Layout en CARRUSEL horizontal: los planes nunca se apilan en varias filas.
 * Caben hasta 4 en pantalla grande; si hay más, el track desborda en horizontal
 * (barra de scroll OCULTA — clase `.no-scrollbar`) y aparecen flechas para
 * moverse. Las cards NO usan `reveal`: así ninguna queda invisible fuera de
 * vista del carrusel (evita el fallo de "el plan no sale").
 */
export function Pricing() {
  const { pricing } = useLandingContent();
  const [annual, setAnnual] = useState(false);

  // Carrusel: track + flechas contextuales (solo si desborda y queda hacia ese lado).
  const trackRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const max = scrollWidth - clientWidth;
    setCanLeft(scrollLeft > 8);
    setCanRight(scrollLeft < max - 8);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
    // pricing.length: recalcular cuando llegan los planes del CMS (carga diferida).
  }, [updateArrows, pricing.length, annual]);

  const scrollByCards = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>("[data-card]");
    const gap = 20; // gap-5 = 1.25rem
    const step = card ? card.offsetWidth + gap : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  if (!pricing.length) return null;

  // El switch solo tiene sentido si hay al menos un plan cuyo precio cambie con él:
  // los "solo anual" muestran siempre su precio anual, así que no cuentan.
  const anyAnnual = pricing.some((p) => !p.annualOnly && (p.priceAnnual || p.priceMonthly));
  const anyAnnualOnly = pricing.some((p) => p.annualOnly);

  return (
    <section id="precios" className="py-24 lg:py-32 bg-neutral-50">
      <div className="max-w-7xl mx-auto px-5 lg:px-6">
        <div className="text-center max-w-2xl mx-auto mb-10 lg:mb-14 reveal">
          <span className="text-xs font-bold uppercase tracking-widest text-primary">Precios</span>
          <h2 className="text-3xl lg:text-5xl font-bold tracking-tight mt-3">
            Un plan para cada etapa de tu negocio.
          </h2>
          <p className="text-muted-foreground mt-4">Sin contratos forzosos. Escala cuando lo necesites.</p>
        </div>

        {/* Switch Mensual / Anual */}
        {anyAnnual && (
          <div className="flex flex-col items-center gap-2 mb-12 reveal">
            <div className="inline-flex items-center rounded-full border border-black/10 bg-white p-1 shadow-sm">
              <button
                onClick={() => setAnnual(false)}
                className={`px-5 py-2 text-sm font-semibold rounded-full transition-all ${!annual ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
              >
                Mensual
              </button>
              <button
                onClick={() => setAnnual(true)}
                className={`px-5 py-2 text-sm font-semibold rounded-full transition-all inline-flex items-center gap-2 ${annual ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
              >
                Anual
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${annual ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"}`}>
                  Ahorra
                </span>
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              {annual ? "El precio mostrado es anual." : "Paga anual y ahorra. El precio mostrado es por mes."}
              {!annual && anyAnnualOnly && " Los planes de solo contratación anual muestran siempre su precio anual."}
            </p>
          </div>
        )}

        {/* Carrusel: relative para anclar las flechas sobre el track. */}
        <div className="relative">
          {/* Flecha izquierda — solo si se puede retroceder. */}
          {canLeft && (
            <button
              type="button"
              onClick={() => scrollByCards(-1)}
              aria-label="Ver planes anteriores"
              className="absolute left-0 top-1/2 -translate-y-1/2 z-20 grid place-items-center size-11 rounded-full bg-white/95 backdrop-blur text-foreground shadow-xl border border-black/10 hover:bg-white transition"
            >
              <ChevronLeft className="size-5" />
            </button>
          )}

          <div
            ref={trackRef}
            className="no-scrollbar flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth px-1 py-8"
          >
            {pricing.map((p) => {
              // Plan "solo anual": ignora el switch Mensual y muestra siempre su precio anual.
              // Si le falta priceAnnual NO se recicla el mensual (quedaría mal etiquetado
              // como "/año"): cae al precio libre de respaldo, sin sufijo.
              const yearly = annual || !!p.annualOnly;
              const amount = p.annualOnly
                ? p.priceAnnual || p.price
                : annual
                  ? p.priceAnnual || p.priceMonthly || p.price
                  : p.priceMonthly || p.price;
              const hasNumeric = p.annualOnly ? !!p.priceAnnual : !!(p.priceMonthly || p.priceAnnual);
              const cta = (
                <span
                  className={`block text-center w-full py-3 rounded-xl font-semibold text-sm transition-all ${
                    p.popular ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-foreground text-background hover:bg-neutral-800"
                  }`}
                >
                  {p.ctaLabel || "Solicitar"}
                </span>
              );
              return (
                <div
                  key={p.id}
                  data-card
                  className={`snap-start shrink-0 w-[82%] sm:w-[calc((100%-1.25rem)/2)] lg:w-[calc((100%-3*1.25rem)/4)] relative flex flex-col p-7 rounded-3xl border transition-all ${
                    p.popular ? "bg-foreground text-background border-foreground shadow-2xl lg:scale-[1.03]" : "bg-white border-black/5 hover:shadow-lg"
                  }`}
                >
                  {p.popular && (
                    <span className="absolute -top-3 left-7 px-3 py-1 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider rounded-full">
                      Más popular
                    </span>
                  )}
                  <p className={`text-xs uppercase tracking-widest font-semibold mb-2 ${p.popular ? "text-primary" : "text-muted-foreground"}`}>
                    {p.tag}
                  </p>
                  <h3 className="text-2xl font-bold mb-2">{p.name}</h3>

                  {/* Precio */}
                  <div className="mb-6 min-h-[3.25rem]">
                    <div className="flex items-end gap-1.5">
                      {hasNumeric && <span className={`text-sm mb-1 ${p.popular ? "text-neutral-400" : "text-muted-foreground"}`}>desde</span>}
                      <span className="text-3xl font-bold">{amount}</span>
                      {hasNumeric && <span className={`text-sm mb-1 ${p.popular ? "text-neutral-400" : "text-muted-foreground"}`}>{yearly ? "/año" : "/mes"}</span>}
                    </div>
                    {(p.annualOnly || (hasNumeric && yearly)) && (
                      <div className="flex items-center gap-2 mt-1">
                        {p.annualOnly && (
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.popular ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"}`}>Solo plan anual</span>
                        )}
                        {hasNumeric && yearly && p.annualBadge && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-success-soft text-success">{p.annualBadge}</span>
                        )}
                        {hasNumeric && yearly && !p.annualOnly && p.priceMonthly && (
                          <span className={`text-xs line-through ${p.popular ? "text-neutral-500" : "text-muted-foreground/70"}`}>{p.priceMonthly}/mes</span>
                        )}
                      </div>
                    )}
                    {hasNumeric && yearly && <p className={`text-[11px] mt-1 ${p.popular ? "text-neutral-400" : "text-muted-foreground"}`}>facturado anualmente</p>}
                  </div>

                  <ul className="space-y-3 mb-6">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <Check className="size-4 mt-0.5 shrink-0 text-primary" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {/* mt-auto empuja el CTA al fondo: con las cards a igual altura
                      (el track las estira), todos los botones quedan alineados sin
                      que la cantidad de features cambie su posición. */}
                  <div className="mt-auto pt-2">
                    {p.ctaHref && isExternal(p.ctaHref) ? (
                      <a href={p.ctaHref} target="_blank" rel="noreferrer">{cta}</a>
                    ) : (
                      <Link to={p.ctaHref || "/registro"}>{cta}</Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Flecha derecha — solo si quedan planes hacia la derecha. */}
          {canRight && (
            <button
              type="button"
              onClick={() => scrollByCards(1)}
              aria-label="Ver más planes"
              className="absolute right-0 top-1/2 -translate-y-1/2 z-20 grid place-items-center size-11 rounded-full bg-white/95 backdrop-blur text-foreground shadow-xl border border-black/10 hover:bg-white transition"
            >
              <ChevronRight className="size-5" />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
